import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BusquedaGeoreferencia} from "./mapas/modelos/BusquedaGeoreferencia";
import {EvkMapasCacheService} from "./mapas/servicios/evk-mapas-cache.service";
import {EvkErrorType, HttpConfig, MessageCrudService} from '@evomatik/evk-common';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import * as L from "leaflet";
import {Punto} from "./mapas/modelos/Punto";
import {Marcador} from "./mapas/modelos/Marcador";
import {FeatureCollection} from "geojson";

@Injectable({
    providedIn: 'root'
})
export class EvkMapasService {
    private baseUrl: string | undefined = '';
    public mapa: L.Map;
    public geoJsonLayer: L.GeoJSON<any>;
    private $dataLatLng: BehaviorSubject<any> = new BehaviorSubject<any>({});
    public dataLatLng: Observable<any> = this.$dataLatLng.asObservable();

    constructor(
        public http: HttpClient,
        private messageService: MessageCrudService,
        private cacheService: EvkMapasCacheService,
        @Inject('httpConfig') private config: HttpConfig) {
        if (this.config.api_georeferencia) {
            this.baseUrl = config.api_georeferencia;
        } else {
            this.baseUrl = 'http://10.0.1.61:7070/';
        }
    }

    public busquedaEstructurada<T>(busqueda: BusquedaGeoreferencia): Observable<T> {
        let busquedaEstructurada = busqueda.getBusquedaEstructurada();
        let query = this.baseUrl + busquedaEstructurada;

        let respuestaEnCache = this.cacheService.buscarPeticionEnCache(busquedaEstructurada);
        let observable = null;
        if (respuestaEnCache) {
            observable = of(respuestaEnCache);
        } else {
            this.messageService.snackbarLoading('Buscando información de georeferencia...');
            observable = this.http.get<T>(query)
                .pipe(
                    tap((data: any) => {
                        this.messageService.snackbarDismiss();
                        this.cacheService.guardarPeticionEnCache(busquedaEstructurada, data);
                        return of(data);
                    }),
                    catchError((error) => {
                        let e = <EvkErrorType>{message: "No se pudo encontrar el servicio de búsqueda"};
                        this.messageService.snackbarError(e);
                        throw 'Error in source. Details: ' + error;
                    })
                );
        }
        return observable;
    }

    public busquedaSimple<T>(busqueda: BusquedaGeoreferencia): Observable<T> {
        let busquedaSimple = busqueda.getBusquedaSimple();
        let query = this.baseUrl + busquedaSimple;

        let respuestaEnCache = this.cacheService.buscarPeticionEnCache(busquedaSimple);
        let observable = null;
        if (respuestaEnCache) {
            observable = of(respuestaEnCache);
        } else {
            observable = this.http.get<T>(query)
                .pipe(
                    tap((data: any) => {
                        this.messageService.snackbarDismiss();
                        this.cacheService.guardarPeticionEnCache(busquedaSimple, data);
                        return of(data);
                    }),
                    catchError((error) => {
                        let e = <EvkErrorType>{message: "No se pudo encontrar el servicio de búsqueda"};
                        this.messageService.snackbarError(e);
                        throw 'Error in source. Details: ' + error;
                    })
                );
        }
        return observable;
    }

    public busquedaEnReversa<T>(latitud: number, longitud: number): Observable<T> {
        let query = this.baseUrl + `reverse?lat=${latitud}&lon=${longitud}&format=geojson&polygon_geojson=1`;
        let respuestaEnCache = this.cacheService.buscarPeticionEnCache(query);
        let observable = null;
        if (respuestaEnCache) {
            observable = of(respuestaEnCache);
        } else {
            this.messageService.snackbarLoading('Buscando información de georeferencia...');
            observable = this.http.get<T>(query)
                .pipe(
                    tap((data: any) => {
                        this.messageService.snackbarDismiss();
                        this.cacheService.guardarPeticionEnCache(query, data);
                        return of(data);
                    }),
                    catchError((error) => {
                        let e = <EvkErrorType>{message: "No se pudo encontrar el servicio de búsqueda"};
                        this.messageService.snackbarError(e);
                        throw 'Error in source. Details: ' + error;
                    })
                );
        }
        return observable;
    }

    public moverAPunto(punto: Punto) {
        if (punto) {
            this.mapa.panTo(new L.LatLng(punto.latitud, punto.longitud));
        }
    }

    public moverAPuntoConZoom(punto: Punto, zoom: number) {
        if (punto) {
            let opciones = {
                duration: 0.25,
                easeLinearity: 0.25,
                noMoveStart: false,
                animate: true
            };
            this.mapa.flyTo(new L.LatLng(punto.latitud, punto.longitud), zoom, opciones);
        }
    }

    public agregarMarker(marcador: Marcador, opciones: any) {
        return L.marker([marcador.punto.latitud, marcador.punto.longitud], opciones).addTo(this.mapa);
    }

    public quitarLayerMarker(capa) {
        this.mapa.removeLayer(capa);
    }

    public getLayer(idLayer) {
        return this.geoJsonLayer.getLayer(idLayer);
    }

    /**
     * En esta funcion es donde se puede modificar las opciones de los elementos del mapa
     * @param jsonFeature
     * @param popupProperty
     */
    public agregarFeature(jsonFeature: FeatureCollection, popupProperty?: string) {
        let mapIdObj = {};
        L.geoJSON(jsonFeature, {
            onEachFeature: this.onEachFeatureClosure(mapIdObj, popupProperty),
            pointToLayer: (feature, latlng) => {
                if (feature.properties.radius) {
                    return new L.Circle(latlng, {radius: feature.properties.radius, color: feature.properties.color});
                } else {
                    let marcador = new L.Marker(latlng, {draggable: true});
                    marcador.setIcon(this.crearIconoParaMarcador());
                    marcador.on("dragend", this.parentEventMoveMarker.bind(this));
                    return marcador;
                }
            },
        }).addTo(this.geoJsonLayer);
        return mapIdObj;
    }

    public parentEventMoveMarker($event: any) {
        this.$dataLatLng.next($event.target['_latlng']);
    }

    private crearIconoParaMarcador() {
        let icono = L.icon({
            iconAnchor: [12, 41],
            iconSize: [25, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
            tooltipAnchor: [16, -28],
            iconUrl: 'leaflet/marker-icon.png',
            shadowUrl: 'leaflet/marker-shadow.png',
            iconRetinaUrl: 'leaflet/marker-icon-2x.png'
        });
        return icono;
    }

    onEachFeatureClosure(mapIdObj, popupProperty?: string) {
        return function onEachFeature(feature, layer) {
            mapIdObj[feature.properties.place_id] = {idLayer: L.stamp(layer), layer: layer};
            if (feature.properties && feature.properties[popupProperty]) {
                layer.bindPopup(feature.properties[popupProperty]);
            }
        }
    }

    quitarTodasLasLayers() {
        this.geoJsonLayer.clearLayers();
    }
}
